import { DraftEntityInstance } from "draft-convert";
import { DraftInlineStyle } from "draft-js";
import React, { useState } from "react";
import styled, { css } from "styled-components";
import { useIsMobile } from "@utils/hooks";
import { Z } from "@ui/styles/zIndex";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { LinkIcon, ListIcon } from "@ui/components/icons";

export type InlineStyle = "ITALIC" | "BOLD" | "UNDERLINE" | "LIST" | "LINK";

const U = styled.span`
	text-decoration: underline;
`;

const richButtonInfos: { inlineStyle: InlineStyle; content: React.ReactNode }[] = [
	{
		inlineStyle: "BOLD",
		content: <b>B</b>,
	},
	{
		inlineStyle: "ITALIC",
		content: <em>I</em>,
	},
	{
		inlineStyle: "UNDERLINE",
		content: <U>U</U>,
	},
	{
		inlineStyle: "LIST",
		content: (
			<div style={{ marginBottom: -2 }}>
				<ListIcon />
			</div>
		),
	},
	{
		inlineStyle: "LINK",
		content: (
			<div>
				<LinkIcon />
			</div>
		),
	},
];

interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
	currentStyle: DraftInlineStyle;
	onStyleChosen: (style: InlineStyle) => void;
	currentEntity?: DraftEntityInstance;
	visibleToolbar?: boolean;
	visibleLink?: boolean;
}
export const Toolbar: React.FC<ToolbarProps> = ({
	currentStyle,
	onStyleChosen,
	visibleToolbar,
	visibleLink,
	currentEntity,
	...htmlProps
}) => {
	const [listActive, setListActive] = useState(false);

	const handleClick = (e: React.MouseEvent | React.TouchEvent, style: InlineStyle): void => {
		e.preventDefault(); // Prevent focus loss
		if (style === "LIST") {
			setListActive(!listActive);
		}
		onStyleChosen(style);
	};

	const richButtons = visibleLink ? richButtonInfos.filter((item) => item.inlineStyle === "LINK") : richButtonInfos;

	const isMobile = useIsMobile();

	const richButtonsInfoOne = isMobile ? richButtonInfos : richButtonInfos.filter((item) => item.inlineStyle !== "LINK");

	return visibleToolbar ? (
		<Container {...htmlProps}>
			{richButtonsInfoOne.map(({ inlineStyle, content }) => {
				const selected =
					(inlineStyle === "LIST" && listActive) ||
					(inlineStyle === "LINK" && currentEntity?.getType() === "LINK" && currentEntity?.getData().url.length > 0) ||
					currentStyle.has(inlineStyle);

				return (
					<RichButton
						key={inlineStyle}
						onMouseDown={(e) => handleClick(e, inlineStyle)}
						onTouchEnd={(e) => handleClick(e, inlineStyle)}
						selected={selected}
					>
						{content}
					</RichButton>
				);
			})}
		</Container>
	) : (
		<RoundedContainer {...htmlProps} onMouseDown={(e) => e.preventDefault()} onTouchEnd={(e) => e.preventDefault()}>
			{richButtons.map(({ inlineStyle, content }) => {
				const selected =
					(inlineStyle === "LIST" && listActive) ||
					(inlineStyle === "LINK" && currentEntity?.getType() === "LINK" && currentEntity?.getData().url.length > 0) ||
					currentStyle.has(inlineStyle);

				return (
					<RichButton
						key={inlineStyle}
						onMouseDown={(e) => handleClick(e, inlineStyle)}
						onTouchEnd={(e) => handleClick(e, inlineStyle)}
						selected={selected}
					>
						{content}
					</RichButton>
				);
			})}
		</RoundedContainer>
	);
};

const RoundedContainer = styled(RoundedBlock)`
	padding: 2px 10px;
	display: flex;
	align-items: center;
	justify-content: space-evenly;

	&::after {
		content: "";
		position: absolute;
		background-color: ${({ theme }) => theme.colors.backgroundPrimary};
		border-bottom-right-radius: 4px;
		border-right: 1px solid ${({ theme }) => theme.colors.lightergray};
		border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};
		z-index: ${Z.under};
		width: 14px;
		height: 14px;
		left: 50%;
		bottom: -1px;
		transform: translate(-50%, 50%) rotate(45deg);
	}
`;

const Container = styled.div`
	padding: 2px 10px;
	display: flex;
	align-items: center;
`;

const RichButton = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>`
	cursor: pointer;
	padding: 14px;
	${({ theme }) => theme.textStyles.secondary};
	font-weight: 600;
	${({ theme, selected }) =>
		selected &&
		css`
			color: ${theme.colors.primary};
			svg .main-path {
				fill: ${theme.colors.primary};
			}
			svg {
				fill: ${theme.colors.primary};
			}
		`};
`;
